import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Bed, Bath, Ruler, MapPin, Calendar, Phone, Mail,
  ChevronLeft, ChevronRight, X, Heart, Share2, CheckCircle, 
  Home, Car, Trees, Wifi, Loader2, Clock, User, Building
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PropertyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isArabic } = useLanguage();
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_date: '',
    preferred_time: '',
    message: ''
  });

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await fetch(`${API_URL}/api/listings/${id}`);
      if (response.ok) {
        const data = await response.json();
        setProperty(data);
      } else {
        toast({ title: "Property not found", variant: "destructive" });
        navigate('/properties');
      }
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  };

  const handleBookViewing = async (e) => {
    e.preventDefault();
    if (!bookingForm.name || !bookingForm.phone || !bookingForm.preferred_date) {
      toast({ title: "Please fill required fields", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/viewing-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          property_id: property.id,
          property_title: property.title,
          property_address: `${property.address}, ${property.city}`,
          ...bookingForm
        })
      });

      if (response.ok) {
        toast({
          title: isArabic ? "تم إرسال طلبك!" : "Viewing Request Submitted!",
          description: isArabic 
            ? "سنتواصل معك لتأكيد الموعد"
            : "We'll contact you to confirm the appointment"
        });
        setShowBookingForm(false);
        setBookingForm({ name: '', email: '', phone: '', preferred_date: '', preferred_time: '', message: '' });
      } else {
        const error = await response.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Please try again", variant: "destructive" });
    }
    setSubmitting(false);
  };

  const nextImage = () => {
    if (property?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % property.images.length);
    }
  };

  const prevImage = () => {
    if (property?.images?.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + property.images.length) % property.images.length);
    }
  };

  const featureIcons = {
    parking: Car,
    garden: Trees,
    wifi: Wifi,
    furnished: Home,
    // Add more as needed
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF8C42]" />
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <p>Property not found</p>
      </div>
    );
  }

  const images = property.images?.length > 0 
    ? property.images 
    : ['https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800'];

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 py-4">
        <Link to="/properties" className="inline-flex items-center text-gray-600 hover:text-[#FF8C42]">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {isArabic ? 'العودة للعقارات' : 'Back to Properties'}
        </Link>
      </div>

      {/* Image Gallery */}
      <div className="max-w-7xl mx-auto px-4 mb-8">
        <div className="relative rounded-2xl overflow-hidden bg-gray-200">
          {/* Main Image */}
          <div 
            className="aspect-[16/9] md:aspect-[21/9] cursor-pointer relative group"
            onClick={() => setShowGallery(true)}
          >
            <img
              src={images[currentImageIndex]}
              alt={property.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 text-white bg-black/50 px-4 py-2 rounded-lg">
                {isArabic ? `عرض ${images.length} صورة` : `View all ${images.length} photos`}
              </span>
            </div>
            
            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            {/* Image Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm">
              {currentImageIndex + 1} / {images.length}
            </div>
          </div>

          {/* Thumbnail Strip */}
          {images.length > 1 && (
            <div className="flex gap-2 p-4 bg-white overflow-x-auto">
              {images.slice(0, 10).map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 transition-colors ${
                    currentImageIndex === idx ? 'border-[#FF8C42]' : 'border-transparent'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
              {images.length > 10 && (
                <button
                  onClick={() => setShowGallery(true)}
                  className="flex-shrink-0 w-20 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-sm text-gray-600"
                >
                  +{images.length - 10}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Property Details */}
      <div className="max-w-7xl mx-auto px-4 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  property.listing_type === 'rent' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  {property.listing_type === 'rent' 
                    ? (isArabic ? 'للإيجار' : 'To Rent')
                    : (isArabic ? 'للبيع' : 'For Sale')
                  }
                </span>
                <span className="text-sm text-gray-500 capitalize">{property.property_type}</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#404040] mb-2">
                {property.title}
              </h1>
              <p className="text-lg text-gray-600 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#FF8C42]" />
                {property.address}, {property.city} {property.postcode}
              </p>
            </div>

            {/* Price */}
            <div className="bg-gradient-to-r from-[#FF8C42] to-[#FF6B35] text-white p-6 rounded-xl">
              <div className="text-4xl font-bold">
                £{property.price?.toLocaleString()}
                {property.listing_type === 'rent' && <span className="text-lg font-normal"> /month</span>}
              </div>
              {property.listing_type === 'rent' && property.deposit_amount && (
                <p className="text-sm opacity-90 mt-1">Deposit: £{property.deposit_amount?.toLocaleString()}</p>
              )}
            </div>

            {/* Key Features */}
            <Card className="shadow-lg border-none">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-[#404040] mb-4">
                  {isArabic ? 'المواصفات الرئيسية' : 'Key Features'}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <Bed className="w-8 h-8 mx-auto mb-2 text-[#FF8C42]" />
                    <div className="text-2xl font-bold">{property.bedrooms}</div>
                    <div className="text-sm text-gray-500">{isArabic ? 'غرف نوم' : 'Bedrooms'}</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-xl">
                    <Bath className="w-8 h-8 mx-auto mb-2 text-[#FF8C42]" />
                    <div className="text-2xl font-bold">{property.bathrooms}</div>
                    <div className="text-sm text-gray-500">{isArabic ? 'حمامات' : 'Bathrooms'}</div>
                  </div>
                  {property.floor_area && (
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <Ruler className="w-8 h-8 mx-auto mb-2 text-[#FF8C42]" />
                      <div className="text-2xl font-bold">{property.floor_area}</div>
                      <div className="text-sm text-gray-500">sq ft</div>
                    </div>
                  )}
                  {property.available_from && (
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <Calendar className="w-8 h-8 mx-auto mb-2 text-[#FF8C42]" />
                      <div className="text-sm font-bold">{property.available_from}</div>
                      <div className="text-sm text-gray-500">{isArabic ? 'متاح من' : 'Available'}</div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <Card className="shadow-lg border-none">
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-[#404040] mb-4">
                  {isArabic ? 'الوصف' : 'Description'}
                </h2>
                <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                  {property.description || 'No description available.'}
                </p>
              </CardContent>
            </Card>

            {/* Features */}
            {property.features?.length > 0 && (
              <Card className="shadow-lg border-none">
                <CardContent className="p-6">
                  <h2 className="text-xl font-bold text-[#404040] mb-4">
                    {isArabic ? 'المميزات' : 'Features & Amenities'}
                  </h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {property.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-gray-600">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        <span className="capitalize">{feature.replace('_', ' ')}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar - Booking */}
          <div className="space-y-6">
            <Card className="shadow-xl border-none sticky top-24">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-[#404040] mb-4 text-center">
                  {isArabic ? 'مهتم بهذا العقار؟' : 'Interested in this property?'}
                </h3>
                
                <Button 
                  onClick={() => setShowBookingForm(true)}
                  className="w-full bg-[#FF8C42] hover:bg-[#FF6B35] text-white py-6 text-lg mb-4"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  {isArabic ? 'احجز معاينة' : 'Book a Viewing'}
                </Button>

                <div className="text-center text-gray-500 text-sm mb-4">
                  {isArabic ? 'أو تواصل معنا مباشرة' : 'Or contact us directly'}
                </div>

                <div className="space-y-3">
                  <a 
                    href="tel:02081898889"
                    className="flex items-center justify-center gap-2 p-3 border-2 border-gray-200 rounded-lg hover:border-[#FF8C42] transition-colors"
                  >
                    <Phone className="w-5 h-5 text-[#FF8C42]" />
                    <span>020 8189 8889</span>
                  </a>
                  <a 
                    href="mailto:info@harryandjacobs.com"
                    className="flex items-center justify-center gap-2 p-3 border-2 border-gray-200 rounded-lg hover:border-[#FF8C42] transition-colors"
                  >
                    <Mail className="w-5 h-5 text-[#FF8C42]" />
                    <span>info@harryandjacobs.com</span>
                  </a>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                    <Building className="w-4 h-4" />
                    <span>Harry & Jacobs Estate Agents</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Full Gallery Modal */}
      {showGallery && (
        <div className="fixed inset-0 bg-black z-50 flex flex-col">
          <div className="flex justify-between items-center p-4 text-white">
            <span>{currentImageIndex + 1} / {images.length}</span>
            <button onClick={() => setShowGallery(false)} className="p-2 hover:bg-white/10 rounded-full">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="flex-1 flex items-center justify-center relative">
            <img
              src={images[currentImageIndex]}
              alt=""
              className="max-h-full max-w-full object-contain"
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 bg-white/20 hover:bg-white/40 p-3 rounded-full"
                >
                  <ChevronLeft className="w-8 h-8 text-white" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 bg-white/20 hover:bg-white/40 p-3 rounded-full"
                >
                  <ChevronRight className="w-8 h-8 text-white" />
                </button>
              </>
            )}
          </div>

          {/* Thumbnails */}
          <div className="p-4 overflow-x-auto">
            <div className="flex gap-2 justify-center">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentImageIndex(idx)}
                  className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 ${
                    currentImageIndex === idx ? 'border-[#FF8C42]' : 'border-transparent opacity-50'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Booking Form Modal */}
      {showBookingForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg">
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-[#404040]">
                  {isArabic ? 'احجز معاينة' : 'Book a Viewing'}
                </h3>
                <button onClick={() => setShowBookingForm(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="font-medium text-[#404040]">{property.title}</p>
                <p className="text-sm text-gray-500">{property.address}, {property.city}</p>
              </div>

              <form onSubmit={handleBookViewing} className="space-y-4">
                <div>
                  <Label>{isArabic ? 'الاسم الكامل *' : 'Full Name *'}</Label>
                  <Input
                    value={bookingForm.name}
                    onChange={(e) => setBookingForm({...bookingForm, name: e.target.value})}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{isArabic ? 'البريد الإلكتروني' : 'Email'}</Label>
                    <Input
                      type="email"
                      value={bookingForm.email}
                      onChange={(e) => setBookingForm({...bookingForm, email: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>{isArabic ? 'الهاتف *' : 'Phone *'}</Label>
                    <Input
                      type="tel"
                      value={bookingForm.phone}
                      onChange={(e) => setBookingForm({...bookingForm, phone: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{isArabic ? 'التاريخ المفضل *' : 'Preferred Date *'}</Label>
                    <Input
                      type="date"
                      value={bookingForm.preferred_date}
                      onChange={(e) => setBookingForm({...bookingForm, preferred_date: e.target.value})}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  <div>
                    <Label>{isArabic ? 'الوقت المفضل' : 'Preferred Time'}</Label>
                    <select
                      value={bookingForm.preferred_time}
                      onChange={(e) => setBookingForm({...bookingForm, preferred_time: e.target.value})}
                      className="w-full p-2 border rounded-md"
                    >
                      <option value="">{isArabic ? 'أي وقت' : 'Any time'}</option>
                      <option value="09:00">9:00 AM</option>
                      <option value="10:00">10:00 AM</option>
                      <option value="11:00">11:00 AM</option>
                      <option value="12:00">12:00 PM</option>
                      <option value="14:00">2:00 PM</option>
                      <option value="15:00">3:00 PM</option>
                      <option value="16:00">4:00 PM</option>
                      <option value="17:00">5:00 PM</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>{isArabic ? 'رسالة (اختياري)' : 'Message (optional)'}</Label>
                  <Textarea
                    value={bookingForm.message}
                    onChange={(e) => setBookingForm({...bookingForm, message: e.target.value})}
                    rows={3}
                    placeholder={isArabic ? 'أي متطلبات خاصة...' : 'Any special requirements...'}
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-[#FF8C42] hover:bg-[#FF6B35] text-white py-6"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-5 h-5 mr-2" />
                  )}
                  {isArabic ? 'إرسال الطلب' : 'Submit Request'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
